import { useEffect, useRef, useCallback } from 'react';
import { throttle } from 'lodash';
import {
  setAccessToken,
  setShowVideoo,
  setUserData,
  setOnBoarding,
  setSidebarIsOpens,
} from '../store/action';

const useIdleLogout = (dispatch, timeoutMinutes = 30, accessToken = null) => {
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);
  const throttledHandlerRef = useRef(null);

  const logout = useCallback(() => {
    console.log('🔴 [IDLE LOGOUT] User has been idle - logging out...');

    // Clear all localStorage items
    localStorage.removeItem('rainLearnerData');
    localStorage.removeItem('rainLearnerToken');
    localStorage.removeItem('rainLearnerLang');
    localStorage.removeItem('languagesObj');
    localStorage.removeItem('isActionDisabled');
    localStorage.removeItem('rainRMUserData');
    localStorage.removeItem('rainLearnerCourseId');
    localStorage.removeItem('rainClientUserData');
    localStorage.removeItem('rainRoute');
    localStorage.removeItem('lastActivity');

    // Clear session storage
    sessionStorage.clear();

    // Dispatch Redux actions to clear state
    dispatch(setUserData(''));
    dispatch(setShowVideoo(false));
    dispatch(setOnBoarding(false));
    dispatch(setAccessToken(''));
    dispatch(setSidebarIsOpens(false));

    console.log('🔴 [IDLE LOGOUT] Redirecting to home page...');
    // Redirect to home
    window.location.href = '/';
  }, [dispatch]);

  const checkIdleTime = useCallback(() => {
    const idleTime = Date.now() - lastActivityRef.current;
    const timeoutMs = timeoutMinutes * 60 * 1000;
    const remainingTime = timeoutMs - idleTime;

    console.log(`⏱️ [IDLE CHECK] Idle time: ${Math.floor(idleTime / 1000)}s, Timeout: ${Math.floor(timeoutMs / 1000)}s, Remaining: ${Math.floor(remainingTime / 1000)}s`);

    if (idleTime >= timeoutMs) {
      console.log('⚠️ [IDLE CHECK] Timeout reached - triggering logout');
      logout();
    } else {
      console.log(`⏳ [IDLE CHECK] Setting next check in ${Math.floor(remainingTime / 1000)}s`);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(checkIdleTime, timeoutMs - idleTime);
    }
  }, [logout, timeoutMinutes]);

  useEffect(() => {
    // Only run idle logout if user is logged in (has access token)
    if (!accessToken) {
      console.log('⚪ [IDLE LOGOUT HOOK] User not logged in - hook disabled');
      return;
    }

    console.log(`🟢 [IDLE LOGOUT HOOK] Initialized with ${timeoutMinutes} minute(s) timeout`);

    // Create throttled handler - runs max once per second
    throttledHandlerRef.current = throttle(() => {
      const currentTime = Date.now();
      lastActivityRef.current = currentTime;
      localStorage.setItem('lastActivity', currentTime.toString());
      console.log('👆 [USER ACTIVITY] Activity detected - timer reset');
    }, 1000);

    // Check stored activity on mount
    const storedLastActivity = localStorage.getItem('lastActivity');
    if (storedLastActivity) {
      const storedTime = parseInt(storedLastActivity, 10);
      const timeSinceLastActivity = Date.now() - storedTime;
      console.log(`📦 [INIT] Found stored activity from ${Math.floor(timeSinceLastActivity / 1000)}s ago`);
      lastActivityRef.current = storedTime;
      checkIdleTime();
    } else {
      console.log('📦 [INIT] No stored activity found - starting fresh');
    }

    // Start timer
    console.log(`⏰ [INIT] Starting idle timer for ${timeoutMinutes} minute(s)`);
    timerRef.current = setTimeout(checkIdleTime, timeoutMinutes * 60 * 1000);

    // Add event listeners
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    console.log(`👂 [INIT] Adding event listeners for: ${events.join(', ')}`);
    events.forEach(event =>
      window.addEventListener(event, throttledHandlerRef.current)
    );

    // Handle tab visibility
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👁️ [TAB VISIBLE] Tab became visible - checking idle time');
        checkIdleTime();
      } else {
        console.log('👁️ [TAB HIDDEN] Tab is now hidden');
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Sync across tabs
    const handleStorageChange = (e) => {
      if (e.key === 'lastActivity' && e.newValue) {
        console.log('🔄 [SYNC] Activity synced from another tab');
        lastActivityRef.current = parseInt(e.newValue, 10);
      }
      if (e.key === 'rainLearnerToken' && !e.newValue) {
        console.log('🔄 [SYNC] Token removed in another tab - logging out');
        window.location.href = '/';
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Cleanup
    return () => {
      console.log('🧹 [CLEANUP] Removing event listeners and clearing timers');
      events.forEach(event =>
        window.removeEventListener(event, throttledHandlerRef.current)
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
      clearTimeout(timerRef.current);
      if (throttledHandlerRef.current) {
        throttledHandlerRef.current.cancel();
      }
    };
  }, [checkIdleTime, timeoutMinutes, accessToken]);
};

export default useIdleLogout;

